<template>
  <teleport to="body">
    <transition name="panel">
      <div v-if="modelValue" class="side-panel-overlay" @click="onOverlayClick">
        <transition :name="'panel-swap-' + swapDirection" :duration="300">
          <div class="side-panel" :class="panelSideClass" :style="{ width: width }" :key="contentKey" @click.stop>
            <button class="close-button" :style="{ top: closeButtonTop }" @click="closePanel" type="button">×</button>
            <slot />
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  width: {
    type: String,
    default: '580px'
  },
  side: {
    type: String,
    default: 'right',
    validator: (v) => ['left', 'right', 'top'].includes(v)
  },
  lockScroll: {
    type: Boolean,
    default: false
  },
  closeOnOverlay: {
    type: Boolean,
    default: false
  },
  contentKey: {
    //Change the key to swap panels while staying open
    type: [String, Number],
    default: 0
  },
  swapDirection: {
    //forward: the new panel slides in over the current one. back: the current panel slides out, revealing the new one beneath.
    type: String,
    default: 'forward',
    validator: (v) => ['forward', 'back'].includes(v)
  },
  closeButtonTop: {
    //Move the close button down when the panel has its own sticky header at the top
    type: String,
    default: '15px'
  }
})

const emit = defineEmits(['update:modelValue'])

const panelSideClass = computed(() => 'side-panel--' + props.side)

const closePanel = () => {
  emit('update:modelValue', false)
}

const onOverlayClick = () => {
  if (props.closeOnOverlay) {
    closePanel()
  }
}

watch(() => props.modelValue, (isOpen) => {
  if (props.lockScroll) {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }
})
</script>

<style scoped>
.panel-enter-active, .panel-leave-active {
  transition: opacity 0.3s ease-out;
}

.panel-enter-from, .panel-leave-to {
  opacity: 0;
}

.panel-enter-to, .panel-leave-from {
  opacity: 1;
}

.side-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  /* Above the navbar (#nav is z-index:1030). A slide-in panel is an overlay — it must cover the chrome,
     including the top nav bar. (The child .side-panel / close-button z-indexes are local to this context.) */
  z-index: 1040;
}

.side-panel {
  position: fixed;
  top: 0;
  height: 100%;
  background-color: var(--bo-surface);
  overflow-y: auto;
  max-width: 100vw;
  transition: transform 0.3s ease-out;
}

/* Right side (default) */
.side-panel--right {
  right: 0;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
}

.panel-enter-active .side-panel--right {
  animation: slide-in-right 0.3s ease-out;
}

.panel-leave-active .side-panel--right {
  animation: slide-out-right 0.3s ease-out;
}

/* Left side */
.side-panel--left {
  left: 0;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
}

.panel-enter-active .side-panel--left {
  animation: slide-in-left 0.3s ease-out;
}

.panel-leave-active .side-panel--left {
  animation: slide-out-left 0.3s ease-out;
}

/* Top center */
.side-panel--top {
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 100%;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.panel-enter-active .side-panel--top {
  animation: slide-in-top 0.3s ease-out;
}

.panel-leave-active .side-panel--top {
  animation: slide-out-top 0.3s ease-out;
}

@media (max-width: 768px) {
  .side-panel {
    width: 100vw !important;
    left: 0;
    right: 0;
  }
  /* On mobile, always slide from right regardless of side prop */
  .side-panel--left {
    left: auto;
    right: 0;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  }
  .panel-enter-active .side-panel--left {
    animation: slide-in-right 0.3s ease-out;
  }
  .panel-leave-active .side-panel--left {
    animation: slide-out-right 0.3s ease-out;
  }
  /* On mobile, top panel also becomes full-width from right */
  .side-panel--top {
    left: 0;
    right: 0;
    top: 0;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
    transform: none;
  }
  .panel-enter-active .side-panel--top {
    animation: slide-in-right 0.3s ease-out;
  }
  .panel-leave-active .side-panel--top {
    animation: slide-out-right 0.3s ease-out;
  }
}

/* Swapping panels while staying open. Forward: the new panel slides in over the current one. */
.panel-swap-forward-enter-active {
  z-index: 2;
}
.panel-swap-forward-enter-active.side-panel--right {
  animation: slide-in-right 0.3s ease-out;
}
.panel-swap-forward-enter-active.side-panel--left {
  animation: slide-in-left 0.3s ease-out;
}
.panel-swap-forward-enter-active.side-panel--top {
  animation: slide-in-top 0.3s ease-out;
}

/* Back: the current panel slides out, revealing the previous one beneath. */
.panel-swap-back-leave-active {
  z-index: 2;
}
.panel-swap-back-leave-active.side-panel--right {
  animation: slide-out-right 0.3s ease-out;
}
.panel-swap-back-leave-active.side-panel--left {
  animation: slide-out-left 0.3s ease-out;
}
.panel-swap-back-leave-active.side-panel--top {
  animation: slide-out-top 0.3s ease-out;
}

@keyframes slide-in-right {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes slide-out-right {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
}

@keyframes slide-in-left {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes slide-out-left {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
}

@keyframes slide-in-top {
  from {
    transform: translateX(-50%) translateY(-100%);
  }
  to {
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes slide-out-top {
  from {
    transform: translateX(-50%) translateY(0);
  }
  to {
    transform: translateX(-50%) translateY(-100%);
  }
}

.close-button {
  position: sticky;
  top: 15px;
  float: right;
  margin-right: 15px;
  margin-bottom: -55px;
  background-color: color-mix(in srgb, var(--bo-surface) 90%, transparent);
  border-radius: 50%;
  border: none;
  transition: top 0.2s ease-in-out;
  font-size: 45px;
  color: var(--bo-muted);
  cursor: pointer;
  z-index: 1001;
  padding: 0;
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  color: var(--bo-ink);
}
</style>
